import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtRelative } from "../lib/format";
import {
  PageHeader, Panel, Chip, DsButton, Meter,
} from "../ds";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

function OppCard({ opp, onJob, onDismiss }) {
  const companies = opp.distinct_companies || 0;
  const canAct = opp.state !== "job_created" && opp.state !== "dismissed";
  return (
    <div
      className="rounded-[8px] border border-[color:var(--line)] bg-[color:var(--panel)] p-4"
      data-testid={`opp-card-${opp.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <Chip value={opp.state} />
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[color:var(--muted)]">
          last {fmtRelative(opp.last_seen)}
        </span>
      </div>

      <h3 className="font-ui text-base font-bold text-[color:var(--fg)] mt-3">{opp.title}</h3>
      <p className="text-xs text-[color:var(--muted)] mt-1">
        {opp.sector || "—"} ·{" "}
        <span className="text-[color:var(--fg)] font-semibold">{companies}</span> companies asked
      </p>

      <div className="mt-3"><Meter value={companies} max={3} testId={`opp-meter-${opp.id}`} /></div>

      {opp.common_needs?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {opp.common_needs.map((n, i) => (
            <span
              key={i}
              className="text-xs px-2 py-1 rounded-[4px] border border-[color:var(--line-2)] text-[color:var(--fg)]"
            >
              {n}
            </span>
          ))}
        </div>
      )}

      {opp.suggested_mvp && (
        <p className="text-sm text-[color:var(--muted)] mt-3">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)] mr-2">
            MVP
          </span>
          {opp.suggested_mvp}
        </p>
      )}

      {opp.task_id && (
        <p className="text-xs font-mono mt-3" style={{ color: "var(--ok)" }}>
          →{" "}
          <Link to={`/tasks/${opp.task_id}`} className="hover:underline">
            task T-{opp.task_id.slice(-6)}
          </Link>
        </p>
      )}

      {opp.dismissed_reason && (
        <p className="text-xs text-[color:var(--muted)] mt-3">Dismissed: {opp.dismissed_reason}</p>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        {canAct && (
          <>
            <DsButton variant="primary" onClick={() => onJob(opp)} data-testid={`opp-job-${opp.id}`}>
              Create JOB
            </DsButton>
            <DsButton onClick={() => onDismiss(opp)} data-testid={`opp-dismiss-${opp.id}`}>
              Dismiss
            </DsButton>
          </>
        )}
      </div>
    </div>
  );
}

export default function Opportunities() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["opportunities"],
    queryFn: async () => (await api.get("/opportunities")).data,
    refetchInterval: 30000,
  });
  const { data: projects } = useQuery({
    queryKey: ["projects-mini"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const [jobOpp, setJobOpp] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [dismissOpp, setDismissOpp] = useState(null);
  const [reason, setReason] = useState("");

  const groups = useMemo(() => {
    const g = { opportunity: [], watching: [], history: [] };
    for (const o of data?.opportunities || []) {
      if (o.state === "opportunity") g.opportunity.push(o);
      else if (o.state === "watching") g.watching.push(o);
      else g.history.push(o);
    }
    return g;
  }, [data]);

  const confirmJob = async () => {
    if (!projectId) return toast.error("Pick a project first.");
    try {
      const r = await api.post(`/opportunities/${jobOpp.id}/create-job`, { project_id: projectId });
      toast.success("JOB created.");
      setJobOpp(null); setProjectId("");
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      navigate(`/tasks/${r.data.task_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmDismiss = async () => {
    if (!reason.trim()) return toast.error("Reason is required.");
    try {
      await api.post(`/opportunities/${dismissOpp.id}/dismiss`, { reason: reason.trim() });
      toast.success("Dismissed.");
      setDismissOpp(null); setReason("");
      qc.invalidateQueries({ queryKey: ["opportunities"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const Section = ({ title, items, testId }) => (
    <section className="space-y-3" data-testid={testId}>
      <h2 className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">
        {title}
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-[color:var(--muted)]">Nothing here.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((o) => (
            <OppCard key={o.id} opp={o} onJob={setJobOpp} onDismiss={setDismissOpp} />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-8">
      <PageHeader
        title="Opportunities"
        subtitle="Three companies from one sector with the same need open a SaaS opportunity"
      />

      <Section title="OPPORTUNITY" items={groups.opportunity} testId="opps-opportunity" />
      <Section title="WATCHING" items={groups.watching} testId="opps-watching" />
      {groups.history.length > 0 && (
        <Section title="HISTORY" items={groups.history} testId="opps-history" />
      )}

      <Dialog open={!!jobOpp} onOpenChange={(v) => !v && setJobOpp(null)}>
        <DialogContent data-testid="opp-job-dialog" className="bg-[color:var(--panel)] border-[color:var(--line)]">
          <DialogHeader><DialogTitle>Create JOB — pick a project</DialogTitle></DialogHeader>
          <p className="text-sm text-[color:var(--muted)]">
            Creates a <span className="font-mono">prd</span> task in{" "}
            <span className="font-mono">plan_requested</span> state.
          </p>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="opp-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>
              {(projects?.projects || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <DsButton variant="ghost" onClick={() => setJobOpp(null)}>Cancel</DsButton>
            <DsButton variant="primary" onClick={confirmJob} data-testid="opp-job-confirm">
              Create JOB
            </DsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dismissOpp} onOpenChange={(v) => !v && setDismissOpp(null)}>
        <DialogContent data-testid="opp-dismiss-dialog" className="bg-[color:var(--panel)] border-[color:var(--line)]">
          <DialogHeader><DialogTitle>Dismiss opportunity</DialogTitle></DialogHeader>
          <Textarea
            rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why are we dismissing this?"
            data-testid="opp-dismiss-reason"
            className="bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
          />
          <DialogFooter>
            <DsButton variant="ghost" onClick={() => setDismissOpp(null)}>Cancel</DsButton>
            <DsButton variant="no" onClick={confirmDismiss} data-testid="opp-dismiss-confirm">
              Dismiss
            </DsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
